import { useState, useEffect } from "react";
import {
  AlertTriangle, CheckCircle2, Clock, CalendarDays,
  ChevronDown, ChevronUp, Search, Trophy, BookOpen, User
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { utcTimeToLocal } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_TYPE_LABELS = {
  regular: "Regular",
  makeup: "Make-up",
  reschedule: "Reschedule",
  final_exam: "Final Exam",
};

const SESSION_TYPE_STYLES = {
  regular: "bg-[#D6E6FA] text-[#1B5EA0]",
  makeup: "bg-[#FFF3CD] text-[#856404]",
  reschedule: "bg-[#EDE7F6] text-[#512DA8]",
  final_exam: "bg-[#FCE4EC] text-[#C62828]",
};

const formatDate = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return d; }
};

// session times in /admin/student-progress come from class_sessions (stored UTC) —
// convert to the admin's browser local time.
const formatTime = (t, d) => utcTimeToLocal(t, d);

// ── Progress Bar ───────────────────────────────────────────────
const ProgressBar = ({ completed, total }) => {
  const pct = total > 0 ? Math.min((completed / total) * 100, 100) : 0;
  const steps = Array.from({ length: total + 1 }, (_, i) => i); // 0..total

  return (
    <div data-testid="progress-bar">
      <div className="flex items-center gap-2 mb-1">
        <div className="flex-1 relative h-4 bg-[#F0EFEB] rounded-full border border-[#0A0A0A] overflow-hidden">
          <div
            className="h-full rounded-full bg-[#3C8DE4] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-sm font-extrabold w-10 text-right">{Math.round(pct)}%</span>
      </div>
      {/* Step dots */}
      <div className="flex justify-between px-0.5 mt-0.5">
        {steps.map((s) => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full border border-[#0A0A0A] ${
              s <= completed ? "bg-[#3C8DE4]" : "bg-[#F0EFEB]"
            }`}
            title={s === total ? "Final Exam" : s === 0 ? "Start" : `Week ${s}`}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[#4A4A4A] mt-0.5 px-0.5">
        <span>Start</span>
        <span className="font-bold text-[#C62828]">Final Exam</span>
      </div>
    </div>
  );
};

// ── Enrollment Card ────────────────────────────────────────────
const EnrollmentCard = ({ enr }) => {
  const [showCalendar, setShowCalendar] = useState(false);

  return (
    <div className="border border-[#0A0A0A]/10 rounded-xl p-4 bg-white" data-testid={`enrollment-card-${enr.enrollment_id}`}>
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#3C8DE4]" />
            <span className="font-bold">{enr.course_name}</span>
          </div>
          <div className="text-xs text-[#4A4A4A] mt-0.5">Tutor: {enr.tutor_name}</div>
        </div>

        {/* Completion badge */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#F0EFEB]" data-testid="completion-badge">
          <CheckCircle2 className="w-4 h-4 text-[#3C8DE4]" />
          <span className="font-extrabold text-sm">
            {enr.completed_sessions} / {enr.total_sessions}
          </span>
          <span className="text-xs text-[#4A4A4A]">sessions</span>
        </div>
      </div>

      {/* Attendance Alert */}
      {enr.attendance_alert && (
        <div className="mb-3 rounded-lg border border-[#FF9800] bg-[#FFF3E0] px-3 py-2" data-testid="attendance-alert">
          <div className="flex items-center gap-2 text-[#E65100] text-xs font-bold mb-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            Attendance Overdue — {enr.overdue_sessions.length} session{enr.overdue_sessions.length > 1 ? "s" : ""} need marking
          </div>
          <ul className="space-y-0.5">
            {enr.overdue_sessions.map((s) => (
              <li key={s.session_id} className="text-xs text-[#E65100] flex items-center gap-2">
                <Clock className="w-3 h-3 flex-shrink-0" />
                Week {s.week_number} — {formatDate(s.scheduled_date)} {formatTime(s.scheduled_time, s.scheduled_date)}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${SESSION_TYPE_STYLES[s.session_type] || SESSION_TYPE_STYLES.regular}`}>
                  {SESSION_TYPE_LABELS[s.session_type] || "Regular"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Progress bar */}
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#4A4A4A] mb-1.5">
          Overall Progress
        </div>
        <ProgressBar completed={enr.completed_sessions} total={enr.total_sessions} />
      </div>

      {/* Upcoming sessions toggle */}
      <button
        onClick={() => setShowCalendar(v => !v)}
        className="flex items-center gap-2 text-xs font-bold text-[#3C8DE4] hover:underline mt-1"
        data-testid="toggle-calendar"
      >
        <CalendarDays className="w-3.5 h-3.5" />
        {enr.upcoming_sessions.length === 0
          ? "No upcoming sessions"
          : `${enr.upcoming_sessions.length} upcoming session${enr.upcoming_sessions.length > 1 ? "s" : ""}`}
        {enr.upcoming_sessions.length > 0 && (showCalendar ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>

      {showCalendar && enr.upcoming_sessions.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="calendar-list">
          {enr.upcoming_sessions.map((s) => (
            <div key={s.session_id} className="flex items-center gap-2 p-2 rounded-lg bg-[#F0EFEB] border border-[#0A0A0A]/10" data-testid={`upcoming-session-${s.week_number}`}>
              <div className="w-10 text-center shrink-0">
                <div className="text-[9px] uppercase font-bold text-[#4A4A4A]">Wk</div>
                <div className="text-lg font-extrabold leading-none">{s.week_number}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{formatDate(s.scheduled_date)}</div>
                <div className="text-xs text-[#4A4A4A]">{formatTime(s.scheduled_time, s.scheduled_date)}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${SESSION_TYPE_STYLES[s.session_type] || SESSION_TYPE_STYLES.regular}`}>
                {SESSION_TYPE_LABELS[s.session_type] || "Regular"}
                {s.session_type === "final_exam" && <Trophy className="inline w-2.5 h-2.5 ml-1" />}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Student Card ───────────────────────────────────────────────
const StudentCard = ({ student }) => {
  const [expanded, setExpanded] = useState(student.has_alert);

  return (
    <div
      className={`brutalist-card p-5 ${student.has_alert ? "border-[#FF9800]" : ""}`}
      data-testid={`student-card-${student.student_id}`}
    >
      <button
        className="w-full flex items-center justify-between gap-3"
        onClick={() => setExpanded(v => !v)}
        data-testid={`expand-student-${student.student_id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-[#D6E6FA] border-2 border-[#0A0A0A] flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-[#3C8DE4]" />
          </div>
          <div className="text-left min-w-0">
            <div className="font-bold truncate">{student.student_name}</div>
            <div className="text-xs text-[#4A4A4A] truncate">{student.student_email}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {student.has_alert && (
            <span className="flex items-center gap-1 text-xs font-bold text-[#E65100] bg-[#FFF3E0] border border-[#FF9800] px-2 py-0.5 rounded-full" data-testid="alert-badge">
              <AlertTriangle className="w-3 h-3" /> Needs Attention
            </span>
          )}
          <span className="text-xs text-[#4A4A4A] font-medium">
            {student.enrollments.length} course{student.enrollments.length !== 1 ? "s" : ""}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {student.enrollments.length === 0 ? (
            <p className="text-sm text-[#4A4A4A] italic text-center py-4">No active enrollments</p>
          ) : (
            student.enrollments.map((enr) => (
              <EnrollmentCard key={enr.enrollment_id} enr={enr} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────
const AdminStudentProgress = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | alert | ok

  useEffect(() => { loadProgress(); }, []);

  const loadProgress = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/admin/student-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(res.data);
    } catch {
      toast.error("Failed to load student progress");
    } finally {
      setLoading(false);
    }
  };

  const alertCount = students.filter(s => s.has_alert).length;

  const filtered = students.filter(s => {
    const matchSearch = s.student_name.toLowerCase().includes(search.toLowerCase()) ||
      s.student_email.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === "all" ? true :
      filter === "alert" ? s.has_alert :
      !s.has_alert;
    return matchSearch && matchFilter;
  });

  return (
    <div data-testid="admin-student-progress">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">Student Progress</h1>
          <p className="text-sm text-[#4A4A4A] mt-0.5">
            Track attendance, completions, and upcoming sessions for every student
          </p>
        </div>
        <button
          onClick={loadProgress}
          className="brutalist-btn-primary px-4 py-2 text-sm"
          data-testid="refresh-btn"
        >
          Refresh
        </button>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="px-4 py-2 rounded-xl border-2 border-[#0A0A0A] bg-white text-sm font-bold">
          {students.length} students
        </div>
        {alertCount > 0 && (
          <div className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-[#FF9800] bg-[#FFF3E0] text-sm font-bold text-[#E65100]" data-testid="alert-summary">
            <AlertTriangle className="w-4 h-4" />
            {alertCount} need{alertCount === 1 ? "s" : ""} attention
          </div>
        )}
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4A4A]" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="brutalist-input w-full pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          {[
            { key: "all", label: "All" },
            { key: "alert", label: "Needs Attention" },
            { key: "ok", label: "On Track" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-2 rounded-lg border-2 border-[#0A0A0A] text-sm font-bold transition-colors ${
                filter === key ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F0EFEB]"
              }`}
              data-testid={`filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Student list */}
      {loading ? (
        <div className="text-center py-16 text-[#4A4A4A]" data-testid="loading-state">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#4A4A4A]" data-testid="empty-state">
          {search || filter !== "all" ? "No students match your filter." : "No students found."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => (
            <StudentCard key={s.student_id} student={s} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStudentProgress;
